import { useEffect, useMemo, useState } from "react";
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ClockIcon,
  EnvelopeIcon,
  PauseIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { Badge } from "V2.0/common/badge";
import { ConversationSourceIcon } from "V2.0/HomePages/Threads/components/conversation_item";
import { STATUS } from "V2.0/HomePages/Campaigns/common/CampaignStatusBadge";

function formatTimestamp(timestamp) {
  const value = Number(timestamp);
  if (!value || !Number.isFinite(value)) return "—";
  return dayjs(new Date(value)).format("MMM DD, YYYY, HH:mm");
}

function formatShortDate(timestamp) {
  const value = Number(timestamp);
  if (!value || !Number.isFinite(value)) return "—";
  return dayjs(new Date(value)).format("MMM D, HH:mm");
}

function formatDuration(seconds) {
  const value = Number(seconds);
  if (!value || !Number.isFinite(value) || value <= 0) return null;
  const mins = Math.floor(value / 60);
  const secs = Math.round(value % 60);
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
}

function parseDurationToSeconds(duration) {
  if (duration === null || duration === undefined) return 0;
  if (typeof duration === "number") return duration;
  const parts = String(duration).trim().split(":").map(Number);
  if (!parts.length || parts.some(Number.isNaN)) return 0;
  return parts.reduce((acc, val) => acc * 60 + val, 0);
}

// A message's q/a field can be a lone space (" ") when the row is a template
// broadcast or a system event with no real customer/assistant text. Truthiness
// alone treats that as "has content" and renders an empty chat bubble, so every
// check against message.q / message.a must go through this instead.
function hasContent(text) {
  return typeof text === "string" && text.trim().length > 0;
}

// Each conversation now carries its own createdAt/updatedAt directly (added
// after the extractedData fields turned out to be unreliable/absent in the
// thin list payload). Prefer createdAt for ordering and display; fall back to
// the older extractedData / message-timestamp paths for records that predate
// this field. Conversations still in flight (Ongoing) or never connected
// (Unanswered) appear to not have createdAt/extractedData/messages populated
// yet — for those, fall back to the parent campaign's own createdAt as a
// last resort so the node shows a real date instead of "—". This is an
// approximation (all such nodes in one campaign will share that date) — flag
// if a per-attempt timestamp is available elsewhere and we should use that
// instead.
function getConversationTimestamp(conv, campaign) {
  if (conv?.createdAt) {
    const created = new Date(conv.createdAt).getTime();
    if (Number.isFinite(created) && created > 0) return created;
  }

  const iso =
    conv?.extractedData?.conversationStartTime ||
    conv?.extractedData?.callStartTime;
  if (iso) {
    const parsed = new Date(iso).getTime();
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  const messageTimestamps = (conv?.messages || [])
    .flatMap((m) => [Number(m.qTimestamp), Number(m.aTimestamp)])
    .filter((t) => Number.isFinite(t) && t > 0);
  if (messageTimestamps.length) return Math.min(...messageTimestamps);

  if (campaign?.createdAt) {
    const campaignCreated = new Date(campaign.createdAt).getTime();
    if (Number.isFinite(campaignCreated) && campaignCreated > 0) {
      return campaignCreated;
    }
  }
  return 0;
}

// Prefer updatedAt - createdAt as the duration, since it works for every
// conversation type (chat/email/whatsapp), not just voice calls. Falls back
// to extractedData.callDuration for older voice records that predate
// createdAt/updatedAt being set on the conversation itself.
function getConversationDurationSeconds(conv) {
  if (conv?.createdAt && conv?.updatedAt) {
    const start = new Date(conv.createdAt).getTime();
    const end = new Date(conv.updatedAt).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return (end - start) / 1000;
    }
  }
  return parseDurationToSeconds(conv?.extractedData?.callDuration);
}

function getConversationCallId(conv) {
  return conv?.extractedData?.callId || conv?.extractedData?.CallId || null;
}

// Each conversation carries its own status at campaignDetails.callStatus —
// this is per-conversation, not the top-level campaign.callStatus.
function getConversationStatus(conv) {
  return conv?.campaignDetails?.callStatus || null;
}

// Tailwind classes per color name from CAMPAIGN_STATUS_LABEL_COLORS. Kept as
// one lookup so the timeline nodes and any future status UI stay in sync with
// that single source of truth instead of re-deriving colors per component.
const COLOR_NAME_TO_CLASSES = {
  green: { circle: "bg-green-500 border-green-500 text-white", dot: "bg-green-500" },
  violet: { circle: "bg-violet-500 border-violet-500 text-white", dot: "bg-violet-500" },
  yellow: { circle: "bg-yellow-500 border-yellow-500 text-white", dot: "bg-yellow-500" },
  amber: { circle: "bg-amber-500 border-amber-500 text-white", dot: "bg-amber-500" },
  pink: { circle: "bg-pink-500 border-pink-500 text-white", dot: "bg-pink-500" },
  red: { circle: "bg-red-500 border-red-500 text-white", dot: "bg-red-500" },
  blue: { circle: "bg-blue-500 border-blue-500 text-white", dot: "bg-blue-500" },
};
const DEFAULT_COLOR_CLASSES = {
  circle: "bg-slate-400 border-slate-400 text-white",
  dot: "bg-slate-400",
};

// Duplicated from SingleCampaigns.tsx to avoid a circular import
// (SingleCampaigns -> CampaignObjectsTable -> CampaignReportModal -> SingleCampaigns).
// Keep this in sync manually if the source values ever change.
const CAMPAIGN_STATUS = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  ONGOING: "Ongoing",
  FAILED: "Failed",
  UNANSWERED: "Unanswered",
  VOICEMAIL: "Voicemail",
  BLOCKED: "Blocked",
  DELIVERED: "Delivered",
  REJECTED: "Rejected",
  QUEUED: "Queued",
  CallPutOnHold: "CallPutOnHold",
  PLACED: "Placed",
};

const CAMPAIGN_STATUS_LABEL_COLORS = {
  [CAMPAIGN_STATUS.COMPLETED]: "green",
  [CAMPAIGN_STATUS.DELIVERED]: "green",
  [CAMPAIGN_STATUS.VOICEMAIL]: "violet",
  [CAMPAIGN_STATUS.ONGOING]: "yellow",
  [CAMPAIGN_STATUS.SCHEDULED]: "amber",
  [CAMPAIGN_STATUS.UNANSWERED]: "pink",
  [CAMPAIGN_STATUS.FAILED]: "red",
  [CAMPAIGN_STATUS.BLOCKED]: "red",
  [CAMPAIGN_STATUS.REJECTED]: "red",
  [CAMPAIGN_STATUS.QUEUED]: "amber",
  [CAMPAIGN_STATUS.CallPutOnHold]: "blue",
  [CAMPAIGN_STATUS.PLACED]: "amber",
};

// getStatusStyle receives a STATUS VALUE (e.g. "Completed"), never a key, so
// icons must be looked up by value too. CAMPAIGN_STATUS[status] was wrong here:
// it indexed the enum by key (uppercase, e.g. COMPLETED) using a value
// (e.g. "Completed"), which is undefined for nearly every real status and,
// worse, for "CallPutOnHold" resolves to the *string* "CallPutOnHold" itself
// (since key === value there) and gets rendered as an invalid element type.
const STATUS_ICONS = {
  [CAMPAIGN_STATUS.COMPLETED]: CheckIcon,
  [CAMPAIGN_STATUS.DELIVERED]: CheckIcon,
  [CAMPAIGN_STATUS.SCHEDULED]: ClockIcon,
  [CAMPAIGN_STATUS.QUEUED]: ClockIcon,
  [CAMPAIGN_STATUS.ONGOING]: ClockIcon,
  [CAMPAIGN_STATUS.PLACED]: ClockIcon,
  [CAMPAIGN_STATUS.VOICEMAIL]: EnvelopeIcon,
  [CAMPAIGN_STATUS.CallPutOnHold]: PauseIcon,
  [CAMPAIGN_STATUS.FAILED]: XMarkIcon,
  [CAMPAIGN_STATUS.BLOCKED]: XMarkIcon,
  [CAMPAIGN_STATUS.REJECTED]: XMarkIcon,
  [CAMPAIGN_STATUS.UNANSWERED]: XMarkIcon,
};

function getStatusStyle(status) {
  const colorName = CAMPAIGN_STATUS_LABEL_COLORS[status];
  return {
    ...(COLOR_NAME_TO_CLASSES[colorName] || DEFAULT_COLOR_CLASSES),
    icon: STATUS_ICONS[status] || ClockIcon,
  };
}

function getConversationSource(conv, campaign) {
  return conv?.sourceOfConversation || campaign?.sourceOfConversation || null;
}

function ConversationStatusTimeline({ conversations, isVoice, activeId, onSelect, campaign }) {
  const points = useMemo(
    () =>
      conversations.map((conv) => {
        const status = getConversationStatus(conv);
        return {
          id: conv._id,
          ts: getConversationTimestamp(conv, campaign),
          status,
          subLabel: status,
          source: getConversationSource(conv, campaign),
        };
      }),
    [conversations, isVoice, campaign],
  );

  // One legend entry per distinct status actually present in this timeline —
  // avoids listing all possible statuses when only a few ever occur here.
  const legendEntries = useMemo(() => {
    const seen = new Map();
    points.forEach((p) => {
      const label = p.status || "unknown";
      if (!seen.has(label)) {
        seen.set(label, getStatusStyle(p.status).dot);
      }
    });
    return Array.from(seen.entries());
  }, [points]);

  return (
    <div className="mb-3 rounded-xl border border-slate-100 p-4">
      <div className="flex items-start overflow-x-auto pb-1">
        {points.map((p, i) => {
          const style = getStatusStyle(p.status);
          const Icon = style.icon;
          const isActive = p.id === activeId;

          return (
            <div key={p.id} className="flex items-start py-2">
              <button
                type="button"
                onClick={() => onSelect(p.id)}
                className="flex shrink-0 flex-col items-center gap-2 px-2 text-center"
              >
                <span className="relative flex h-9 w-9 items-center justify-center">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full border-2 ${style.circle} ${
                      isActive ? "ring-2 ring-indigo-500 ring-offset-2" : ""
                    }`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  {p.source && ConversationSourceIcon[p.source] && (
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-slate-600 shadow ring-1 ring-slate-200 [&_svg]:h-2.5 [&_svg]:w-2.5">
                      {ConversationSourceIcon[p.source]}
                    </span>
                  )}
                </span>
                <span
                  className={`whitespace-nowrap text-xs ${
                    isActive ? "font-semibold text-slate-900" : "text-slate-500"
                  }`}
                >
                  {formatShortDate(p.ts)}
                </span>
                <span className="whitespace-nowrap text-[11px] text-slate-400">
                  {p.subLabel}
                </span>
              </button>
              {i < points.length - 1 && (
                <div className="mt-[18px] h-px w-10 shrink-0 bg-slate-200 sm:w-16" />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-4">
        {legendEntries.map(([label, dotClass]) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${dotClass}`} />
            <span className="text-xs text-slate-500">{label.toLowerCase()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, action = null, children }) {
  return (
    <div className="mt-6 first:mt-0">
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </p>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center">
      <p className="text-xs text-slate-400">{children}</p>
    </div>
  );
}

function Transcript({ messages }) {
  if (!messages || messages.length === 0) {
    return <EmptyState>No messages in this conversation.</EmptyState>;
  }

  // Some rows have neither q nor a with real content (pure system rows padded
  // with " "). If every message in the array is like that, show the empty
  // state rather than an apparently-populated-but-blank scroll area.
  const anyRealContent = messages.some(
    (m) => hasContent(m.q) || hasContent(m.a),
  );
  if (!anyRealContent) {
    return <EmptyState>No messages in this conversation.</EmptyState>;
  }

  return (
    <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-100 p-3">
      <div className="flex flex-col gap-2.5">
        {messages.map((message, index) => (
          <div key={index} className="flex flex-col gap-2.5">
            {hasContent(message.q) ? (
              <div className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-indigo-50 px-3 py-2">
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                    {message.q}
                  </p>
                  {message.qTimestamp ? (
                    <p className="mt-0.5 text-right text-[10px] text-slate-400">
                      {formatTimestamp(message.qTimestamp)}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
            {hasContent(message.a) ? (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2">
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                    {message.a}
                  </p>
                  {message.aTimestamp ? (
                    <p className="mt-0.5 text-[10px] text-slate-400">
                      {formatTimestamp(message.aTimestamp)}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function Shell({ eyebrow, title, subtitle, badge, onClose, children, footer }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {eyebrow}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-slate-900">
                {title}
              </h2>
              {badge}
            </div>
            {subtitle && (
              <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
}

export default function CampaignReportModal(props) {
  const { isOpen, onClose, campaign, assistantsRecord } = props;
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const timelineConversations = useMemo(
    () =>
      [...(campaign?.conversation || [])].sort(
        (a, b) =>
          getConversationTimestamp(a, campaign) -
          getConversationTimestamp(b, campaign),
      ),
    [campaign],
  );

  const activeConversationId =
    (selectedConversationId &&
      timelineConversations.some((c) => c._id === selectedConversationId) &&
      selectedConversationId) ||
    timelineConversations[timelineConversations.length - 1]?._id;

  const activeConversation = timelineConversations.find(
    (c) => c._id === activeConversationId,
  );

  useEffect(() => {
    setSelectedConversationId(null);
  }, [campaign?._id]);

  if (!isOpen || !campaign) return null;

  const identifier =
    campaign.toEmail || campaign.userIdentifier || "Unknown contact";
  const assistantName =
    assistantsRecord?.[campaign.assistant]?.name ||
    campaign.assistant?.name ||
    null;

  const lastAttemptStatus =
    campaign.sourceOfConversation === "EMAIL"
      ? campaign.emailStatus
      : campaign.callStatus;

  const isVoice = campaign.sourceOfConversation === "OUTBOUNDVOICE";

  const callId = isVoice ? getConversationCallId(activeConversation) : null;

  const duration = formatDuration(getConversationDurationSeconds(activeConversation));

  const activeConversationStatus = getConversationStatus(activeConversation);

  const activeConversationSource = getConversationSource(
    activeConversation,
    campaign,
  );

  const subtitleParts = [
    assistantName,
    `Created ${formatTimestamp(campaign.createdAt)}`
  ].filter(Boolean);

  return (
    <Shell
      eyebrow="Campaign Report"
      title={identifier}
      subtitle={subtitleParts.join(" · ")}
      badge={
        <>
          {lastAttemptStatus && (
            <Badge color={CAMPAIGN_STATUS_LABEL_COLORS[lastAttemptStatus]}>
              {lastAttemptStatus}
            </Badge>
          )}
          {campaign.status === STATUS.ARCHIVED && <Badge>Archived</Badge>}
        </>
      }
      onClose={onClose}
      footer={
        <button
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
        >
          Close
        </button>
      }
    >
      {/* <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
          {ConversationSourceIcon[campaign.sourceOfConversation]}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">Contact</p>
          <p className="truncate text-sm font-semibold text-slate-900">
            {identifier}
          </p>
        </div>
        {campaign.fromIdentifier && (
          <div className="ml-auto min-w-0 text-right">
            <p className="text-xs text-slate-500">From</p>
            <p className="truncate text-sm font-medium text-slate-700">
              {campaign.fromIdentifier}
            </p>
          </div>
        )}
      </div> */}

      {timelineConversations.length > 1 && (
        <Section icon={ClockIcon} title="Timeline">
          <ConversationStatusTimeline
            conversations={timelineConversations}
            isVoice={isVoice}
            activeId={activeConversationId}
            onSelect={setSelectedConversationId}
            campaign={campaign}
          />
        </Section>
      )}

      {callId && (
        <div className="flex items-baseline gap-2 rounded-xl bg-slate-50 px-4 py-2.5">
          <p className="shrink-0 text-xs text-slate-500">Call ID</p>
          <p className="min-w-0 select-all break-all font-mono text-xs text-slate-700">
            {callId}
          </p>
        </div>
      )}

      <Section
        icon={ChatBubbleLeftRightIcon}
        title="Conversation"
        action={
          <div className="flex items-center gap-2">
            {activeConversationStatus && (
              <Badge color={CAMPAIGN_STATUS_LABEL_COLORS[activeConversationStatus]}>
                {activeConversationStatus}
              </Badge>
            )}
          </div>
        }
      >
        {timelineConversations.length === 0 ? (
          <EmptyState>
            This contact was never reached, so there is no conversation to
            show.
          </EmptyState>
        ) : (
          <Transcript messages={activeConversation?.messages} />
        )}
      </Section>
    </Shell>
  );
}